import { Injectable, signal } from '@angular/core';
import { DateTime } from 'luxon';
import { UserDb } from 'services/user-db.service';
import { SettingsService } from 'services/settings.service';
import { PlanMerge, WorkoutLoaderService } from 'services/workout-loader.service';
import { combinedEntryForExercise, Log_Workout } from '@cf-shared/data-model/log-workout';
import { LogRecordState } from '@cf-shared/data-model/user-log';
import { SetSuccess, SetType } from '@cf-shared/enums';
import { ProgressChartSeries, ProgressChartDataPoint, ProgressChartAxisConfig } from 'components/cs-progress-chart/cs-progress-chart';
import { seriesUnit } from 'components/cs-progress-chart/progress-chart-units';
import { DocWatcher } from 'services/database-wrapper';
import { LL } from 'i18n/i18n.service';

export interface ExerciseProgressOptions {
  includeRMTests?: boolean;
}

/**
 * Data provider service for exercise progress charts.
 * Provides series configuration and data fetching for weight, 1RM, and tonnage.
 */
@Injectable({ providedIn: 'root' })
export class ChartDataExerciseProgressService {

  // loading state signal - set to true while fetching data
  isLoading = signal(false);

  constructor(
    private userDb: UserDb,
    private settings: SettingsService,
    private workoutLoader: WorkoutLoaderService
  ) {}

  /**
   * Get series configuration for exercise progress chart.
   * Returns series for weight, 1RM, and tonnage.
   *
   * @param weightUnit - per-exercise sticky unit (ExerciseStat.weight_unit).
   *                    The series are shown in it (displayUnit), and the chart
   *                    converts the kg data to it. It can differ from the global
   *                    addedweight_kg pref, which is the fallback, then the region default (#3297).
   */
  getSeries(showWeight: boolean, show1RM: boolean, showTonnage: boolean, weightUnit?: string): ProgressChartSeries[] {
    // the axis unit comes from the same helper the chart converts with, so the two cannot disagree
    const unit = seriesUnit('addedweight_kg', weightUnit, this.settings.locale, type => this.settings.getUnitPref(type)).unitName ?? '';
    return [
      {
        id: 'weight',
        label: LL.exerciseStats.chart.weight(),
        colorVar: '--chart-turquoise',
        yAxisId: 'y-1-weight',
        yAxisLabel: LL.exerciseStats.chart.weightAxis({ unit }),
        measurementTypeId: 'addedweight_kg',
        displayUnit: weightUnit,
        hidden: !showWeight,
        tooltipExtraField: 'reps',
        tooltipExtraLabel: 'reps',
        precision: 0.1
      },
      {
        id: '1rm',
        label: LL.exerciseStats.chart.oneRMEstimate(),
        colorVar: '--chart-purple',
        yAxisId: 'y-2-1rm',
        yAxisLabel: LL.exerciseStats.chart.oneRMAxis({ unit }),
        measurementTypeId: 'addedweight_kg',
        displayUnit: weightUnit,
        hidden: !show1RM,
        precision: 0.1
      },
      {
        id: 'tonnage',
        label: LL.exerciseStats.chart.tonnage(),
        colorVar: '--chart-blue',
        yAxisId: 'y-3-tonnage',
        yAxisLabel: LL.exerciseStats.chart.tonnageAxis({ unit }),
        measurementTypeId: 'addedweight_kg',
        displayUnit: weightUnit,
        hidden: !showTonnage,
        precision: 1
      }
    ];
  }

  /**
   * Get axis configuration for exercise progress chart.
   * Uses stacked Y-axes for separate measurement scales.
   */
  getAxisConfig(): ProgressChartAxisConfig {
    return { stacked: true };
  }

  /**
   * Fetch exercise progress data for the given date range.
   *
   * @param exerciseId - Exercise ID to fetch data for
   * @param startDate - ISO date string for range start (optional)
   * @param endDate - ISO date string for range end (optional)
   * @param options - Optional configuration (includeRMTests)
   * @returns Array of data points with date, weight, reps, 1rm, and tonnage values, in kg (the chart converts them)
   */
  async fetchDataAsync(
    exerciseId: string,
    watcher: DocWatcher,
    startDate?: string,
    endDate?: string,
    options?: ExerciseProgressOptions
  ): Promise<ProgressChartDataPoint[]> {
    this.isLoading.set(true);

    try {
      const includeRMTests = options?.includeRMTests ?? false;

      // fetch all workouts for this exercise
      const allWorkouts = await this.userDb.getWorkoutsForExerciseAsync(exerciseId);

      if (allWorkouts.length === 0) {
        return [];
      }

      // load plan for getEffectiveExercises
      const plan = await this.userDb.loadPlanAsync(watcher);

      // group workouts by date
      const workoutsByDate: { [key: string]: Log_Workout } = {};
      for (const w of allWorkouts) {
        const date = DateTime.fromISO(w.time_utc).toLocal().toISODate();
        workoutsByDate[date] = w;
      }

      // determine date range
      const firstDate = DateTime.fromISO(allWorkouts[0].time_utc);
      const lastDate = DateTime.fromISO(allWorkouts[allWorkouts.length - 1].time_utc);

      // optionally filter by provided date range
      const rangeStart = startDate ? DateTime.fromISO(startDate) : firstDate;
      const rangeEnd = endDate ? DateTime.fromISO(endDate) : lastDate;

      // build contiguous date array and extract data
      const dataPoints: ProgressChartDataPoint[] = [];

      for (let d = rangeStart; d <= rangeEnd; d = d.plus({ days: 1 })) {
        const dayKey = d.toLocal().toISODate();
        const workout = workoutsByDate[dayKey];

        let weight_kg: number | undefined;
        let rep: number | undefined;
        let rm_kg: number | undefined;
        let tonnage_kg: number | undefined;

        if (workout) {
          const exercises = this.workoutLoader.getEffectiveExercises(workout, plan, PlanMerge.AsStored);
          // every entry of this exercise in the workout, not just the first (#5507)
          const exercise = combinedEntryForExercise(exercises, exerciseId);
          if (exercise) {
            for (const set of exercise.sets) {
              // skip null sets and RM tests if not included
              if (!set || (!includeRMTests && [SetType.RMTest, SetType.RMTestFollow].includes(set.setType))) {
                continue;
              }
              // a skipped set was never performed. Changing an exercise after logging sets marks
              // the old exercise's remaining sets skipped (#5507), so without this their planned
              // numbers would be charted as lifted
              if (set.state === LogRecordState.Skipped) {
                continue;
              }
              // only include successful sets
              if (set.setSuccess !== SetSuccess.Fail) {
                // track max weight and its reps
                if (!weight_kg || set.stats.weight_kg > weight_kg) {
                  weight_kg = set.stats.weight_kg;
                  rep = set.stats.reps;
                }
                // track max 1RM estimate
                if (!rm_kg || set.stats.estimated1rm_kg > rm_kg) {
                  rm_kg = set.stats.estimated1rm_kg;
                }
                // accumulate tonnage
                tonnage_kg = (tonnage_kg || 0) + set.stats.weight_kg * set.stats.reps;
              }
            }
          }
        }

        // create data point in kg; the chart converts to the series' display unit
        const dataPoint: ProgressChartDataPoint = {
          date: dayKey
        };

        if (weight_kg !== undefined) {
          dataPoint['weight'] = weight_kg;
          dataPoint['reps'] = rep;
        }
        if (rm_kg !== undefined) {
          dataPoint['1rm'] = rm_kg;
        }
        if (tonnage_kg !== undefined) {
          dataPoint['tonnage'] = tonnage_kg;
        }

        dataPoints.push(dataPoint);
      }

      return dataPoints;
    } finally {
      this.isLoading.set(false);
    }
  }
}
